import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../../../meet/NavLinks';
import HostNavLinks from '../../HostNavLinks';
import MyMeetsNavLinks from '../../../myMeets/MyMeetsNavLinks';
import HostRegistrationConstants from '../HostRegistrationConstants';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetDateValidationData from '../../../../state/omeMeetDateValidation/UseOmeMeetDateValidationData';
import useMyMeetsFiltersData from '../../../../state/myMeetsFilters/UseMyMeetsFiltersData';

import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useMeetData from '@usas/ui-web/state/meet/UseMeetData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';
import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useOrgUnitData from '@usas/ui-web/state/orgUnit/UseOrgUnitData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

import { omeMeetsIsComplete } from '../../../../components/leftNavigation/LeftNavValidation';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDateTimeTwelveHourClock } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_VIEW_STATE = {
  submitButton: '',
  tryRedirect: false,
  tryGet: false,
  error: ''
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete OME Meet?',
  omeMeetName: '',
  meetEntryStartDate: '',
  meetEntryEndDate: '',
  timeZone: '',
  status: '',
  omeMeetEntryId: Constants.DEFAULT_ID
};

const useOmeMeets = () => {
  const navigate = useNavigate();
  const { omeMeetState } = useOmeMeetData();
  const { omeMeetEntryState, getOmeMeetEntry, deleteOmeMeetEntry } = useOmeMeetEntryData();
  const { omeMeetDateValidationState, getOmeMeetDateValidation } = useOmeMeetDateValidationData();
  const { meetState } = useMeetData();
  const { isReadyToRoute, navRoutes } = useNavRoutes();
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { myMeetsFiltersState } = useMyMeetsFiltersData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { orgUnitState } = useOrgUnitData();
  const { TEAM_OME_FEE_TYPE_ID, CREDENTIAL_OME_FEE_TYPE_ID, INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, INTL_STAFF_OME_FEE_TYPE_ID, RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID,
    ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, FINA_ORG_UNIT_ID } = useEnvironmentVariableData();
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);

  const onAddOmeMeet = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route !== '') {
      navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL, { state: { omeMeetEntryId: Constants.DEFAULT_ID } });
    }
    else {
      navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL, { state: { omeMeetEntryId: Constants.DEFAULT_ID } });
    }
  }

  const onEditOmeMeet = (e, omeMeetEntry) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route !== '') {
      navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL, { state: { omeMeetEntryId: omeMeetEntry.omeMeetEntryId } });
    }
    else {
      navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL, { state: { omeMeetEntryId: omeMeetEntry.omeMeetEntryId } });
    }
  };

  const onDeleteOmeMeetClicked = (e, omeMeetEntry) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setModalState({
      ...modalState,
      displayPopUp: true,
      omeMeetName: omeMeetEntry.omeMeetName || '',
      meetEntryStartDate: omeMeetEntry?.meetEntryStartDate === null ? '' : formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryStartDate),
      meetEntryEndDate: omeMeetEntry?.meetEntryEndDate === null ? '' : formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryEndDate),
      timeZone: omeMeetEntry.timeZone || '',
      status: omeMeetEntry.status || '',
      omeMeetEntryId: omeMeetEntry.omeMeetEntryId || Constants.DEFAULT_ID
    });
  };

  const onDeleteOmeMeet = (e, omeMeetEntryId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    onModalCanceled();
    deleteOmeMeetEntry(omeMeetEntryId);
    setState({ ...state, tryGet: true });
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: HostRegistrationConstants.CONTINUE, tryRedirect: true });
  };

  const onSaveAndExitButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: HostRegistrationConstants.SAVE_AND_EXIT, tryRedirect: true });
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: HostRegistrationConstants.BACK, tryRedirect: true });
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.objData, omeMeetEntryState.objData]);

  useEffect(() => {
    if (omeMeetEntryState.isSaving === false && omeMeetEntryState.isSaved === true &&
      state.tryGet === true) {
      getOmeMeetEntry(omeMeetState.objData?.omeMeetId);
      getOmeMeetDateValidation(omeMeetState.objData?.omeMeetId);
      if (state.tryGet === true) {
        setState({ ...state, tryGet: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetEntryState, state.tryGet])

  useEffect(() => {
    if (isReadyToRoute === true && meetState.isSaving === false && state.tryRedirect === true) {
      if (state.submitButton === HostRegistrationConstants.CONTINUE) {
        const omeMeetsDetailRoutePermission = navRoutes?.OME_HOST_OME_MEETS_DETAIL?.permission;
        if (omeMeetsIsComplete(omeMeetState.objData, orgUnitState.arrayData, meetState.objData, omeMeetEntryState.objData, omeMeetsDetailRoutePermission, TEAM_OME_FEE_TYPE_ID, CREDENTIAL_OME_FEE_TYPE_ID,
          INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, INTL_STAFF_OME_FEE_TYPE_ID, RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID,
          ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, FINA_ORG_UNIT_ID) === false) {
          if (omeMeetEntryState.objData?.omeMeetEntry?.length <= 1) {
            setState({ ...state, error: 'The main OME Meet must be complete before continuing to Submit. Changes made on other pages may have caused an OME Meet with a "Complete" status to no longer be complete. Please edit the OME Meet, and provide any missing information.' });
          } else {
            setState({ ...state, error: 'All OME Meets must be complete before continuing to Submit. Changes made on other pages may have caused OME Meets with "Complete" statuses to no longer be complete. Please edit each OME Meet, and provide any missing information.' });
          }
        }
        else {
          if (omeMeetState.route !== '') {
            navigate(NavLinks.MEET_HOST_SUBMIT);
          }
          else {
            navigate(HostNavLinks.OME_MEET_SUBMIT);
          }
        }
      }
      else if (state.submitButton === HostRegistrationConstants.SAVE_AND_EXIT) {
        if (omeMeetState.route !== '') {
          setSearchMeetState({ ...searchMeetState, showMeetHeader: true });
          navigate(NavLinks.MEET_HOST_MANAGEMENT, { state: { tryGetOmeMeet: true } });
        }
        else {
          navigate(MyMeetsNavLinks.MEET_HOST, { state: { selectOrgUnitState: selectOrgUnitState, myMeetsFiltersState: myMeetsFiltersState } });
        }
      }
      else if (state.submitButton === HostRegistrationConstants.BACK) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_EVENTS);
        }
        else {
          navigate(HostNavLinks.OME_MEET_EVENTS);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState.isSaving, state, isReadyToRoute]);

  function allowEdits() {
    if (omeMeetState.objData?.status === Constants.WORKFLOW_STATUS_CREATED) {
      if (omeMeetDateValidationState.objData?.isCurrentDateBeforeMeetEntryChangeDeadline === true) {
        return true;
      }
      return false;
    }

    return true;
  }

  return {
    state,
    omeMeetEntryState,
    modalState,
    onModalCanceled,
    onAddOmeMeet,
    onEditOmeMeet,
    onDeleteOmeMeetClicked,
    onDeleteOmeMeet,
    onNextButtonClicked,
    onBackButtonClicked,
    onSaveAndExitButtonClicked,
    allowEdits
  };
};

export default useOmeMeets;